import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAdmins } from "@/features/admins/api/use-admins";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useUpsertInsightFeedback } from "../../api/use-claim-insights";

/**
 * Jurist feedback on an AI insight: a thumb, then the note that explains it.
 *
 * The thumb is written as soon as it is clicked — a jurist who walks away
 * mid-note still leaves the signal — and the note is a second, upserted write
 * on the same row. That ordering is the whole point: what the legal team needs
 * out of this is the *written* reason, and asking for it after the click costs
 * nothing if it never comes.
 *
 * Restores the feedback block of the pre-V042026 EligibilityInsight.vue, which
 * only ever asked for a comment on rejection.
 */

type ClaimInsight = NonNullable<ClaimWithRelations["insights"]>[number];

interface InsightFeedbackProps {
  claimId: string;
  insight: ClaimInsight | null | undefined;
  /** e.g. "Cette étude est-elle correcte ?" */
  question: string;
  className?: string;
}

export function InsightFeedback({ claimId, insight, question, className }: InsightFeedbackProps) {
  const { user, can } = useCurrentUser();
  const { data: admins } = useAdmins();
  const upsertFeedback = useUpsertInsightFeedback(claimId);

  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  // The thumb just clicked, before the claim refetch lands — the note must be
  // saved under it, never under a stale (or missing) stored feedback.
  const [pendingThumb, setPendingThumb] = useState<boolean | null>(null);

  if (!can([...UPCLAIM_ROLES, "LAWYER"]) || !insight?.id) {
    return null;
  }

  const insightType = insight.type?.type ?? "";
  const feedbacks = insight.adminFeedbacks ?? [];
  // The API upserts per (insight, admin); the current admin is matched by email
  // since the browser only knows its Auth0 identity, not the admin row's id.
  const myAdminId = admins?.find((admin) => admin.email && admin.email === user?.email)?.id ?? null;
  const myFeedback = myAdminId ? feedbacks.find((feedback) => feedback.adminId === myAdminId) : undefined;
  const otherFeedbacks = feedbacks.filter((feedback) => feedback !== myFeedback);
  const adminName = (adminId: string) =>
    admins?.find((admin) => admin.id === adminId)?.fullName ?? "Un admin";

  const currentThumb = pendingThumb ?? myFeedback?.isAccepted ?? null;

  function submit(isAccepted: boolean, comment?: string) {
    setPendingThumb(isAccepted);
    upsertFeedback.mutate(
      { insightId: insight?.id ?? "", isAccepted, comment, insightType },
      {
        // A thumb alone opens the note right after it is saved; saving the note closes it.
        onSuccess: () => {
          setNoteOpen(comment === undefined);
          setNote(comment ?? "");
        },
      },
    );
  }

  /** Flip the vote, keeping the note already written under it. */
  function switchThumb(isAccepted: boolean) {
    if (isAccepted === currentThumb) {
      return;
    }
    submit(isAccepted, myFeedback?.comment ?? "");
  }

  function openNote() {
    setNote(myFeedback?.comment ?? "");
    setNoteOpen(true);
  }

  return (
    <div className={cn("border-t border-purple-100 pt-4", className)}>
      {/* Keyed on the thumb, not on `myFeedback`: the email→admin match can miss
          (admins list unavailable, address mismatch) and a jurist who just voted
          must never be shown the question again as if nothing had been sent. */}
      {currentThumb !== null ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {/* Both thumbs stay clickable: a vote is an opinion, not a commitment. */}
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              aria-label="Approuver"
              aria-pressed={currentThumb === true}
              disabled={upsertFeedback.isPending}
              onClick={() => switchThumb(true)}
              className={cn(
                "rounded-md p-1",
                currentThumb ? "bg-emerald-50 text-emerald-600" : "text-gray-400 hover:text-emerald-600",
              )}
            >
              <ThumbsUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Rejeter"
              aria-pressed={currentThumb === false}
              disabled={upsertFeedback.isPending}
              onClick={() => switchThumb(false)}
              className={cn(
                "rounded-md p-1",
                currentThumb ? "text-gray-400 hover:text-rose-600" : "bg-rose-50 text-rose-600",
              )}
            >
              <ThumbsDown className="h-4 w-4" />
            </button>
          </div>
          <span className="font-medium text-gray-700">{currentThumb ? "Approuvée" : "Rejetée"}</span>
          {myFeedback?.comment && !noteOpen ? (
            <span className="min-w-0 flex-1 truncate text-gray-500 italic">« {myFeedback.comment} »</span>
          ) : null}
          {!noteOpen ? (
            <button type="button" className="link ml-auto text-xs" onClick={openNote}>
              {myFeedback?.comment ? "Modifier la note" : "Ajouter une note"}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-2 text-sm tracking-tight text-gray-600">{question}</span>
          <Button
            size="sm"
            variant="outline"
            disabled={upsertFeedback.isPending}
            onClick={() => submit(true)}
          >
            <ThumbsUp className="text-emerald-600" />
            Oui
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={upsertFeedback.isPending}
            onClick={() => submit(false)}
          >
            <ThumbsDown className="text-rose-600" />
            Non
          </Button>
        </div>
      )}

      {noteOpen ? (
        <div className="mt-3 space-y-2">
          <Textarea
            rows={3}
            autoFocus
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={
              currentThumb === false
                ? "Qu'est-ce qui est faux ? Dates, cause retenue, réglementation citée, itinéraire…"
                : "Ce qui est juste, ce qui manque, ce qui reste à vérifier à la main…"
            }
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={upsertFeedback.isPending}
              onClick={() => submit(currentThumb ?? false, note)}
            >
              Enregistrer la note
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setNoteOpen(false)}>
              Annuler
            </Button>
          </div>
        </div>
      ) : null}

      {otherFeedbacks.length > 0 ? (
        <ul className="mt-3 space-y-1 text-xs text-gray-500">
          {otherFeedbacks.map((feedback) => (
            <li key={feedback.id} className="flex items-start gap-1.5">
              {feedback.isAccepted ? (
                <ThumbsUp className="mt-0.5 h-3 w-3 flex-none text-emerald-600" />
              ) : (
                <ThumbsDown className="mt-0.5 h-3 w-3 flex-none text-rose-600" />
              )}
              <span>
                <span className="font-medium">{adminName(feedback.adminId)}</span>
                {feedback.comment ? ` — « ${feedback.comment} »` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
